/**
 * RequireTwoFactor — wrap a page that holds something sensitive (2026-09-26, infrastructure for later).
 *
 *   <RequireTwoFactor><PayrollExportPage /></RequireTwoFactor>
 *
 * - Two-step verification not turned on → a card that says so and links to /security (and back here).
 * - Turned on, but this session hasn't proven a second method within `maxAgeMinutes` (default 30, the
 *   backend guard's default) → a "Confirm it's you" card with the step-up button.
 * - Otherwise renders the children.
 *
 * This only decides what to SHOW. The page's own queries/mutations must also sit behind the backend's
 * `requireTwoFactor` middleware — that is the security; this is the courtesy.
 *
 * `onlyWhenSiteRequires` (2026-09-28): gate ONLY when this site has "ask for two-step verification on
 * sensitive data" switched on (`tenantSecuritySettings.requireTwoFactorForSensitiveData`, off by
 * default, set by a site admin on /security). Mirrors the backend's
 * `requireTwoFactor({ onlyWhenTenantRequires: true })`. With the switch off the children render as
 * before. `withSiteTwoFactor(Page)` wraps a route component the same way.
 */
import React from "react";
import { Box, Button, Heading, Icon, Stack, Text } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FiLock, FiShield } from "react-icons/fi";
import { useAuth } from "../../../contexts/AuthContext";
import { MY_TWO_FACTOR_STATUS, TENANT_SECURITY_SETTINGS } from "../queries";
import { SECURE_PAGE_FRESH_MINUTES, isFreshAal2, readStoredAssurance } from "../utils/assurance";
import type { TwoFactorStatus, TenantSecuritySettings } from "../../../generated/graphql";
import { useStepUp } from "../hooks/useStepUp";
import { AuthError, AuthLoading, AuthSurface, AuthTip, alpha, useAuthTheme } from "./ui";

export type RequireTwoFactorGate = "loading" | "error" | "setup" | "step-up" | "open";

/** Pure: which of the three truths the wrapper shows. Unit-tested. */
export function requireTwoFactorGate(
    status: Pick<TwoFactorStatus, "enabled"> | null | undefined,
    assurance: Parameters<typeof isFreshAal2>[0],
    maxAgeMinutes: number = SECURE_PAGE_FRESH_MINUTES,
    nowSeconds?: number,
): Exclude<RequireTwoFactorGate, "loading" | "error"> {
    if (!status?.enabled) return "setup";
    return isFreshAal2(assurance, maxAgeMinutes, nowSeconds) ? "open" : "step-up";
}

const GateCard: React.FC<{ icon: any; title: string; children: React.ReactNode; action: React.ReactNode }> = ({ icon, title, children, action }) => {
    const t = useAuthTheme();
    return (
        <AuthSurface maxW="560px" mx="auto" my={{ base: 6, md: 12 }} textAlign="center" py={{ base: 8, md: 10 }}>
            <Stack spacing={4} align="center">
                <Box w={14} h={14} borderRadius="2xl" bg={alpha(t.primary, 0.12)} display="flex" alignItems="center" justifyContent="center">
                    <Icon as={icon} boxSize={6} color={t.primary} />
                </Box>
                <Heading size="md" color={t.text}>{title}</Heading>
                <Text fontSize="md" color={t.muted}>{children}</Text>
                {action}
            </Stack>
        </AuthSurface>
    );
};

/**
 * Does this site ask for two-step verification on sensitive data? `null` while loading or when the
 * answer is unknown (error) — callers treat unknown as "yes" so a failed read never opens the gate
 * (the server would refuse the data anyway).
 */
export function useSiteRequiresTwoFactor(): { requires: boolean | null; loading: boolean; error?: Error; refetch: () => void } {
    const { data, loading, error, refetch } = useQuery<{ tenantSecuritySettings: TenantSecuritySettings }>(TENANT_SECURITY_SETTINGS, { fetchPolicy: "cache-and-network" });
    const value = data?.tenantSecuritySettings?.requireTwoFactorForSensitiveData;
    return { requires: typeof value === "boolean" ? value : null, loading, error: error as Error | undefined, refetch: () => { void refetch(); } };
}

export interface RequireTwoFactorProps {
    children: React.ReactNode;
    maxAgeMinutes?: number;
    /** Gate only when this site has "ask for two-step verification on sensitive data" switched on. */
    onlyWhenSiteRequires?: boolean;
}

export const RequireTwoFactor: React.FC<RequireTwoFactorProps> = ({ onlyWhenSiteRequires, ...rest }) =>
    onlyWhenSiteRequires ? <SiteGate {...rest} /> : <Gate {...rest} />;

/** Asks the site first; renders the page untouched when the switch is off. */
const SiteGate: React.FC<Omit<RequireTwoFactorProps, "onlyWhenSiteRequires">> = (props) => {
    const site = useSiteRequiresTwoFactor();
    if (site.requires === false) return <>{props.children}</>;
    if (site.requires === null && site.loading) {
        return <Box maxW="560px" mx="auto" my={12}><AuthLoading label="Checking this site's security settings" cards={1} columns={{ base: 1 }} /></Box>;
    }
    if (site.requires === null && site.error) {
        return <Box maxW="560px" mx="auto" my={12}><AuthError title="Couldn't check this site's security settings" error={site.error} onRetry={site.refetch} /></Box>;
    }
    return <Gate {...props} />;
};

/**
 * Place INSIDE <RequireTwoFactor>: runs `action` once when the gate opens — e.g. re-load data the
 * server withheld until "Confirm it's you". With `onlyWhenSiteRequires`, it runs only when the site
 * switch is on (with it off nothing was withheld, so there is nothing to re-load).
 */
export const WhenGateOpens: React.FC<{ action: () => void; onlyWhenSiteRequires?: boolean }> = ({ action, onlyWhenSiteRequires }) => {
    const site = useSiteRequiresTwoFactor();
    const done = React.useRef(false);
    const should = !onlyWhenSiteRequires || site.requires === true;
    React.useEffect(() => {
        if (done.current || !should) return;
        done.current = true;
        action();
    }, [action, should]);
    return null;
};

/** Wrap a route component so it asks for two-step verification only when the site requires it. */
export function withSiteTwoFactor<P extends object>(Page: React.ComponentType<P>, maxAgeMinutes?: number): React.FC<P> {
    const Wrapped: React.FC<P> = (props) => (
        <RequireTwoFactor onlyWhenSiteRequires maxAgeMinutes={maxAgeMinutes}>
            <Page {...props} />
        </RequireTwoFactor>
    );
    Wrapped.displayName = `withSiteTwoFactor(${Page.displayName || Page.name || "Page"})`;
    return Wrapped;
}

const Gate: React.FC<Omit<RequireTwoFactorProps, "onlyWhenSiteRequires">> = ({ children, maxAgeMinutes = SECURE_PAGE_FRESH_MINUTES }) => {
    const location = useLocation();
    useAuth(); // subscribes: this wrapper re-renders when a step-up changes the session's assurance
    const { requireStepUp, stepUpDialog } = useStepUp({ maxAgeMinutes });
    const { data, loading, error, refetch } = useQuery<{ myTwoFactorStatus: TwoFactorStatus }>(MY_TWO_FACTOR_STATUS, { fetchPolicy: "network-only" });
    const [, tick] = React.useReducer((n: number) => n + 1, 0);

    // A proof goes stale while the page is open: re-check once a minute so the gate closes on time.
    React.useEffect(() => {
        const id = window.setInterval(tick, 60_000);
        return () => window.clearInterval(id);
    }, []);

    if (loading && !data) return <Box maxW="560px" mx="auto" my={12}><AuthLoading label="Checking your two-step verification" cards={1} columns={{ base: 1 }} /></Box>;
    if (error) return <Box maxW="560px" mx="auto" my={12}><AuthError title="Couldn't check your two-step verification" error={error} onRetry={() => refetch()} /></Box>;

    // Read the stored token (always current, even the render right after an adoptToken).
    const gate = requireTwoFactorGate(data?.myTwoFactorStatus, readStoredAssurance(), maxAgeMinutes);
    if (gate === "open") return <>{children}</>;

    const here = `${location.pathname}${location.search}${location.hash}`;
    if (gate === "setup") {
        return (
            <GateCard icon={FiShield} title="This page needs two-step verification" action={
                <AuthTip label="Open your Security page to turn on two-step verification. You'll come back here afterwards.">
                    <Button as={RouterLink} to={`/security?returnTo=${encodeURIComponent(here)}`} colorScheme="blue" size="lg" minH="48px">
                        Turn it on in Security
                    </Button>
                </AuthTip>
            }>
                Pages like this ask you to confirm it's you with two different sign-in methods. Turn on two-step verification on your Security page first.
            </GateCard>
        );
    }
    return (
        <>
            <GateCard icon={FiLock} title="Confirm it's you" action={
                <AuthTip label="Confirm with a second sign-in method (passkey, password, a code or a recovery code). Nothing on your account changes.">
                    <Button colorScheme="blue" size="lg" minH="48px" onClick={() => { void requireStepUp({ force: true, reason: "This page holds sensitive information. Confirm it's you with a second sign-in method." }).then((ok) => { if (ok) tick(); }); }}>
                        Confirm it's you
                    </Button>
                </AuthTip>
            }>
                This page holds sensitive information. Confirm it's you with a second sign-in method to open it.
            </GateCard>
            {stepUpDialog}
        </>
    );
};

export default RequireTwoFactor;
